import React, { useState, useEffect } from "react";
import "./Wastage.css";
import { supabase } from "./supabaseClient";
import { DataGrid } from "@mui/x-data-grid";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";

function Wastage() {
  const today = new Date().toISOString().split("T")[0];

  const [activeTab, setActiveTab] = useState("wastage");

  // Wastage form states
  const [wasteDate, setWasteDate] = useState(today);
  const [wasteAmount, setWasteAmount] = useState("");
  const [wasteNotes, setWasteNotes] = useState("");
  const [wastageData, setWastageData] = useState([]);
  const [editWasteId, setEditWasteId] = useState(null);

  // Stocktake form states
  const [stockDate, setStockDate] = useState(today);
  const [stockDept, setStockDept] = useState("");
  const [stockVariance, setStockVariance] = useState("");
  const [stockData, setStockData] = useState([]);
  const [editStockId, setEditStockId] = useState(null);

  // Filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Fetch Wastage entries
  const fetchWastage = async (start, end) => {
    let query = supabase.from("wastage").select("*").order("date", { ascending: false });
    if (start && end) query = query.gte("date", start).lte("date", end);
    const { data, error } = await query;
    if (error) toast.error("Error fetching wastage entries");
    else setWastageData(data);
  };

  // Fetch Stocktake entries
  const fetchStocktake = async (start, end) => {
    let query = supabase.from("stocktake").select("*").order("date", { ascending: false });
    if (start && end) query = query.gte("date", start).lte("date", end);
    const { data, error } = await query;
    if (error) toast.error("Error fetching stocktake entries");
    else setStockData(data);
  };

  useEffect(() => {
    fetchWastage();
    fetchStocktake();
  }, []);

  // Save Wastage
  const handleSaveWastage = async () => {
    if (!wasteAmount) {
      toast.error("Wastage amount is required.");
      return;
    }
    const { data: { user } } = await supabase.auth.getUser();

    const newEntry = {
      date: wasteDate,
      amount: parseFloat(wasteAmount) || 0,
      notes: wasteNotes,
      logged_by: user?.user_metadata?.display_name || "Manual Entry",
      logged_at: new Date().toISOString()
    };

    let error;
    if (editWasteId) {
      ({ error } = await supabase.from("wastage").update(newEntry).eq("id", editWasteId));
      toast.success("Wastage entry updated!");
    } else {
      ({ error } = await supabase.from("wastage").insert([newEntry]));
      toast.success("Wastage entry added!");
    }

    if (error) toast.error("Error saving wastage entry");
    else {
      resetWasteForm();
      fetchWastage(fromDate, toDate);
    }
  };

  const resetWasteForm = () => {
    setWasteDate(today);
    setWasteAmount("");
    setWasteNotes("");
    setEditWasteId(null);
  };

  const handleEditWastage = (row) => {
    setWasteDate(row.date);
    setWasteAmount(row.amount.toString());
    setWasteNotes(row.notes || "");
    setEditWasteId(row.id);
  };

  const handleDeleteWastage = async (id) => {
    const confirmText = prompt("Type DELETE to confirm:");
    if (confirmText !== "DELETE") {
      toast.info("Delete cancelled.");
      return;
    }
    await supabase.from("wastage").delete().eq("id", id);
    toast.success("Wastage entry deleted.");
    fetchWastage(fromDate, toDate);
  };

  // Save Stocktake
  const handleSaveStocktake = async () => {
    if (!stockDept || !stockVariance) {
      toast.error("Department and variance are required.");
      return;
    }
    const { data: { user } } = await supabase.auth.getUser();

    const newEntry = {
      date: stockDate,
      department: stockDept,
      variance: parseFloat(stockVariance) || 0,
      logged_by: user?.user_metadata?.display_name || "Manual Entry",
      logged_at: new Date().toISOString()
    };

    let error;
    if (editStockId) {
      ({ error } = await supabase.from("stocktake").update(newEntry).eq("id", editStockId));
      toast.success("Stocktake entry updated!");
    } else {
      ({ error } = await supabase.from("stocktake").insert([newEntry]));
      toast.success("Stocktake entry added!");
    }

    if (error) toast.error("Error saving stocktake entry");
    else {
      resetStockForm();
      fetchStocktake(fromDate, toDate);
    }
  };

  const resetStockForm = () => {
    setStockDate(today);
    setStockDept("");
    setStockVariance("");
    setEditStockId(null);
  };

  const handleEditStock = (row) => {
    setStockDate(row.date);
    setStockDept(row.department);
    setStockVariance(row.variance.toString());
    setEditStockId(row.id);
  };

  const handleDeleteStock = async (id) => {
    const confirmText = prompt("Type DELETE to confirm:");
    if (confirmText !== "DELETE") {
      toast.info("Delete cancelled.");
      return;
    }
    await supabase.from("stocktake").delete().eq("id", id);
    toast.success("Stocktake entry deleted.");
    fetchStocktake(fromDate, toDate);
  };

  // Wastage columns
  const wastageColumns = [
    { field: "date", headerName: "Date", width: 110 },
    { field: "amount", headerName: "Amount (£)", width: 140 },
    { field: "notes", headerName: "Notes", width: 250 },
    { field: "logged_by", headerName: "Logged By", width: 140 },
    { field: "logged_at", headerName: "Logged At", width: 170, renderCell: (params) => params.value ? format(new Date(params.value), "dd/MM/yyyy HH:mm") : "" },
    {
      field: "actions", headerName: "Actions", width: 200, renderCell: (params) => (
        <div style={{ display: "flex", gap: "6px" }}>
          <button className="btn-edit" onClick={() => handleEditWastage(params.row)}>Edit</button>
          <button className="btn-delete" onClick={() => handleDeleteWastage(params.row.id)}>Delete</button>
        </div>
      )
    }
  ];

  // Stocktake columns
  const stockColumns = [
    { field: "date", headerName: "Date", width: 110 },
    { field: "department", headerName: "Department", width: 200 },
    { field: "variance", headerName: "Variance (£)", width: 140 },
    { field: "logged_by", headerName: "Logged By", width: 140 },
    { field: "logged_at", headerName: "Logged At", width: 170, renderCell: (params) => params.value ? format(new Date(params.value), "dd/MM/yyyy HH:mm") : "" },
    {
      field: "actions", headerName: "Actions", width: 200, renderCell: (params) => (
        <div style={{ display: "flex", gap: "6px" }}>
          <button className="btn-edit" onClick={() => handleEditStock(params.row)}>Edit</button>
          <button className="btn-delete" onClick={() => handleDeleteStock(params.row.id)}>Delete</button>
        </div>
      )
    }
  ];

  const exportCSV = (data, columns, filename) => {
    const headers = columns.map(c => c.headerName).join(",");
    const csvRows = data.map(r => columns.map(c => r[c.field] ?? "").join(","));
    const blob = new Blob([headers + "\n" + csvRows.join("\n")], { type: "text/csv" });
    saveAs(blob, `${filename}_${format(new Date(), "yyyyMMdd_HHmm")}.csv`);
  };

  return (
    <div className="main-content wastage-container">
      <div className="main-heading">♻️ Wastage & Stocktake</div>
      <div className="tab-buttons" style={{ marginBottom: 24 }}>
        <button className={activeTab === "wastage" ? "active" : ""} onClick={() => setActiveTab("wastage")}>♻️ Wastage</button>
        <button className={activeTab === "stocktake" ? "active" : ""} onClick={() => setActiveTab("stocktake")}>📦 Stocktake</button>
      </div>

      {activeTab === "wastage" && (
        <>
          {/* Wastage Entry Section */}
          <div className="form-section">
            <div className="section-title">♻️ Wastage Entry</div>
            <div className="form-grid">
              <label>
                Date
                <input type="date" value={wasteDate} onChange={(e) => setWasteDate(e.target.value)} />
              </label>
              <label>
                Amount (£)
                <input type="number" step="0.01" min="0" value={wasteAmount} onChange={(e) => setWasteAmount(e.target.value.replace(/[^0-9.]/g, ""))} />
              </label>
              <label>
                Notes
                <input value={wasteNotes} onChange={(e) => setWasteNotes(e.target.value)} />
              </label>
              <div style={{ display: "flex", alignItems: "end", gap: 8 }}>
                <button className="btn-entry" style={{ marginTop: 22 }} onClick={handleSaveWastage}>
                  {editWasteId ? "Update Entry" : "Add Entry"}
                </button>
                <button className="btn-apply" style={{ marginTop: 22, background: "#eee", color: "#222" }} onClick={resetWasteForm}>
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Filter & Export Section */}
          <div className="form-section">
            <div className="section-title">🔍 Filter & Export</div>
            <div className="form-grid">
              <label>
                From
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </label>
              <label>
                To
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </label>
              <button className="btn-apply" style={{ marginTop: 22 }} onClick={() => fetchWastage(fromDate, toDate)}>
                Apply Filter
              </button>
              <button className="btn-export" style={{ marginTop: 22 }} onClick={() => exportCSV(wastageData, wastageColumns, "wastage")}>
                Export CSV
              </button>
            </div>
          </div>

          {/* Data Grid */}
          <div style={{ overflowX: "auto" }}>
            <DataGrid
              rows={wastageData}
              columns={wastageColumns}
              getRowId={(row) => row.id}
              pageSize={5}
              rowsPerPageOptions={[5, 10, 25, 50]}
              autoHeight
              sx={{
                border: "1px solid #ccc",
                "& .MuiDataGrid-cell": {
                  borderRight: "1px solid #ddd !important",
                },
                "& .MuiDataGrid-columnHeaders": {
                  borderBottom: "2px solid #aaa",
                },
                "& .MuiDataGrid-columnSeparator": {
                  visibility: "visible !important",
                  width: "1px !important",
                  backgroundColor: "#ddd",
                },
                "& .MuiDataGrid-row": {
                  borderBottom: "1px solid #eee",
                },
              }}
            />
          </div>
        </>
      )}

      {activeTab === "stocktake" && (
        <>
          {/* Stocktake Entry Section */}
          <div className="form-section">
            <div className="section-title">📦 Stocktake Entry</div>
            <div className="form-grid">
              <label>
                Date
                <input type="date" value={stockDate} onChange={(e) => setStockDate(e.target.value)} />
              </label>
              <label>
                Department
                <input value={stockDept} onChange={(e) => setStockDept(e.target.value)} />
              </label>
              <label>
                Variance (£)
                <input type="number" step="0.01" min="0" value={stockVariance} onChange={(e) => setStockVariance(e.target.value.replace(/[^0-9.]/g, ""))} />
              </label>
              <div style={{ display: "flex", alignItems: "end", gap: 8 }}>
                <button className="btn-entry" style={{ marginTop: 22 }} onClick={handleSaveStocktake}>
                  {editStockId ? "Update Entry" : "Add Entry"}
                </button>
                <button className="btn-apply" style={{ marginTop: 22, background: "#eee", color: "#222" }} onClick={resetStockForm}>
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Filter & Export Section */}
          <div className="form-section">
            <div className="section-title">🔍 Filter & Export</div>
            <div className="form-grid">
              <label>
                From
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </label>
              <label>
                To
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </label>
              <button className="btn-apply" style={{ marginTop: 22 }} onClick={() => fetchStocktake(fromDate, toDate)}>
                Apply Filter
              </button>
              <button className="btn-export" style={{ marginTop: 22 }} onClick={() => exportCSV(stockData, stockColumns, "stocktake")}>
                Export CSV
              </button>
            </div>
          </div>

          {/* Data Grid */}
 {/* Data Grid */}
<div style={{ overflowX: "auto" }}>
  <DataGrid
    rows={stockData}
    columns={stockColumns}
    getRowId={(row) => row.id}
    pageSize={5}
    rowsPerPageOptions={[5, 10, 25, 50]}
    autoHeight
    sx={{
      border: "1px solid #ccc",
      "& .MuiDataGrid-cell": {
        borderRight: "1px solid #ddd !important",
      },
      "& .MuiDataGrid-columnHeaders": {
        borderBottom: "2px solid #aaa",
      },
      "& .MuiDataGrid-columnSeparator": {
        visibility: "visible !important",
        width: "1px !important",
        backgroundColor: "#ddd",
      },
      "& .MuiDataGrid-row": {
        borderBottom: "1px solid #eee",
      },
    }}
  />
</div>
        </>
      )}
    </div>
  );
}

export default Wastage;